import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { FileText, FileX, MailCheck, Mail, Send } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// ─── Badge Components ────────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  if (type === 'STORE') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
        MAĞAZA
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200">
      <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
      ETKİNLİK
    </span>
  );
};

const PdfStatusBadge = ({ storedUrl }) => {
  if (storedUrl) {
    return (
      <div className="flex justify-center" title="PDF Yüklendi">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 shadow-sm transition-transform hover:scale-105">
          <FileText size={16} className="text-emerald-600" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-center" title="PDF Yüklenmedi">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-50 border border-rose-100 shadow-sm transition-transform hover:scale-105">
        <FileX size={16} className="text-rose-500" />
      </div>
    </div>
  );
};

const EmailSentBadge = ({ invoiceEmailSentAt }) => {
  if (invoiceEmailSentAt) {
    const formatted = new Date(invoiceEmailSentAt)?.toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    return (
      <div className="flex justify-center" title={`E-Posta İletildi (${formatted})`}>
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 shadow-sm transition-transform hover:scale-105">
          <MailCheck size={16} className="text-emerald-600" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-center" title="E-Posta Bekliyor">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-50 border border-rose-100 shadow-sm transition-transform hover:scale-105">
        <Mail size={16} className="text-rose-500" />
      </div>
    </div>
  );
};

// ─── PDF Upload Modal ────────────────────────────────────────────────────────

const PdfUploadModal = ({ row, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) { setError('Lütfen bir PDF dosyası seçin'); return; }
    if (file?.type !== 'application/pdf') { setError('Sadece PDF dosyası yüklenebilir'); return; }

    setUploading(true);
    setError('');

    try {
      const ettn = row?.ettn || row?.id;
      const fileName = `unified_invoice_${ettn}.pdf`;

      const { error: uploadError } = await supabase?.storage?.from('invoices')?.upload(fileName, file, { upsert: true, contentType: 'application/pdf' });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase?.storage?.from('invoices')?.getPublicUrl(fileName);
      const publicUrl = urlData?.publicUrl;

      // Update the correct table based on type
      if (row?.type === 'STORE') {
        const { error: updateError } = await supabase?.from('invoice_logs')?.update({ pdf_url: publicUrl })?.eq('id', row?.logId);
        if (updateError) throw updateError;
      } else {
        // EVENT type — update invoice_logs by reservation_id
        const { error: updateError } = await supabase?.from('invoice_logs')?.update({ pdf_url: publicUrl })?.eq('id', row?.logId);
        if (updateError) throw updateError;
      }

      onSuccess(row?.id, publicUrl);
      onClose();
    } catch (err) {
      setError('Yükleme hatası: ' + (err?.message || 'Bilinmeyen hata'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">PDF Yükle</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {row?.customerName || '-'} · {row?.docNo || row?.ettn || '-'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div
            onClick={() => fileRef?.current?.click()}
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
          >
            <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-3" />
            {file ? (
              <p className="text-sm font-medium text-foreground">{file?.name}</p>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">PDF dosyası seçin</p>
                <p className="text-xs text-muted-foreground mt-1">Tıklayın veya sürükleyin</p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => setFile(e?.target?.files?.[0] || null)}
            />
          </div>

          <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
            <p><span className="font-medium">Tür:</span> <TypeBadge type={row?.type} /></p>
            <p><span className="font-medium">ETTN:</span> {row?.ettn || '-'}</p>
            <p><span className="font-medium">Depolama:</span> invoices/unified_invoice_{row?.ettn || row?.id}.pdf</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={uploading}>
              İptal
            </Button>
            <Button className="flex-1" onClick={handleUpload} disabled={uploading || !file}>
              {uploading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Yükleniyor...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="Upload" size={14} />
                  Yükle
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Email Send Modal ────────────────────────────────────────────────────────

const EmailModal = ({ row, onClose, onSuccess }) => {
  // Multi-tier email resolver: tries every possible column
  const resolveEmail = (r) =>
    r?.guest_email ||
    r?.billing_email ||
    r?.email ||
    (r?.user_profiles?.email) ||
    r?.customerEmail ||
    r?.customer_email ||
    (typeof r?.full_name === 'string' && r?.full_name?.includes('@') ? r?.full_name : '') ||
    '';

  const [email, setEmail] = useState(() => resolveEmail(row));
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Force-update email state whenever row changes
  useEffect(() => {
    const resolved = resolveEmail(row);
    setEmail(resolved);
  }, [row]);

  const handleSend = async () => {
    if (!email?.trim()) return;
    setSending(true);
    setResult(null);

    try {
      let endpoint, body;

      if (row?.type === 'STORE') {
        endpoint = `${SUPABASE_URL}/functions/v1/send-invoice-email`;
        body = JSON.stringify({
          logId: row?.logId,
          ettn: row?.ettn || undefined,
          docNo: row?.docNo || undefined,
          customerEmail: email?.trim(),
        });
      } else {
        // EVENT type
        endpoint = `${SUPABASE_URL}/functions/v1/send-invoice-email`;
        body = JSON.stringify({
          logId: row?.logId,
          ettn: row?.ettn || undefined,
          docNo: row?.docNo || undefined,
          customerEmail: email?.trim(),
          reservationId: row?.reservationId || undefined,
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body,
      });

      const data = await response?.json();
      if (!response?.ok) throw new Error(data?.error || `HTTP ${response.status}`);

      // Update DB with sent timestamp
      const nowISO = new Date()?.toISOString();
      await supabase?.from('invoice_logs')?.update({ invoice_email_sent_at: nowISO })?.eq('id', row?.logId);

      setResult({ success: true, message: 'E-posta başarıyla gönderildi!' });
      // Notify parent to update local state with current timestamp
      if (onSuccess) {
        onSuccess(row?.id, nowISO);
      }
    } catch (err) {
      setResult({ success: false, message: err?.message || 'Gönderme hatası' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">Fatura E-postası Gönder</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{row?.customerName || '-'}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">E-posta Adresi</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="ornek@email.com"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
            <p className="flex items-center gap-2"><TypeBadge type={row?.type} /> <span>{row?.docNo || row?.ettn || '-'}</span></p>
            <p><span className="font-medium">PDF:</span> <PdfStatusBadge storedUrl={row?.storedUrl} /></p>
          </div>

          {result && (
            <div className={`rounded-lg p-3 text-sm border ${result?.success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {result?.message}
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Kapat
            </Button>
            <Button className="flex-1" onClick={handleSend} disabled={sending || !email?.trim()}>
              {sending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Gönderiliyor...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="Send" size={14} />
                  Gönder
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const MasterInvoiceCenter = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyMissingPdf, setOnlyMissingPdf] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [uploadModal, setUploadModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePdfConfirm, setDeletePdfConfirm] = useState(null);
  const [deletePdfLoading, setDeletePdfLoading] = useState(false);
  const [replacingPdfId, setReplacingPdfId] = useState(null);
  const replaceFileRefs = useRef({});
  const bulkUploadRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ total: 0, archived: 0, missing: 0, store: 0, event: 0 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSendingBulk, setIsSendingBulk] = useState(false);
  const [emailFilter, setEmailFilter] = useState('ALL'); // 'ALL' | 'SENT' | 'UNSENT'

  // ─── Turkish text normalization for fuzzy matching ──────────────────────────
  const normalizeText = (str) => {
    if (!str) return '';
    return str?.toLocaleLowerCase('tr-TR')?.replace(/ğ/g, 'g')?.replace(/ü/g, 'u')?.replace(/ş/g, 's')?.replace(/ı/g, 'i')?.replace(/ö/g, 'o')?.replace(/ç/g, 'c')?.replace(/[^a-z0-9]/g, '')?.trim();
  };

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/event-discovery');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Step 1: Fetch base invoice_logs without any joins
      const { data: logs, error: logsErr } = await supabase
        ?.from('invoice_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (logsErr) throw logsErr;

      const logsData = logs || [];

      // Step 2: Extract non-null order_ids and reservation_ids
      const orderIds = [...new Set(logsData.filter((l) => l?.order_id).map((l) => l.order_id))];
      const reservationIds = [...new Set(logsData.filter((l) => l?.reservation_id).map((l) => l.reservation_id))];

      // Step 3: Parallel fetch orders and event_registrations
      const [ordersResult, registrationsResult] = await Promise.all([
        orderIds?.length > 0
          ? supabase?.from('orders')?.select('id, customer_name, guest_email, billing_email, email, user_id, full_name')?.in('id', orderIds)
          : Promise.resolve({ data: [], error: null }),
        reservationIds?.length > 0
          ? supabase?.from('event_registrations')?.select('id, participant_name, participant_email')?.in('id', reservationIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      const ordersMap = {};
      (ordersResult?.data || [])?.forEach((o) => { ordersMap[o.id] = o; });

      // Fetch user_profiles emails for orders that have user_id but no guest_email
      const userIds = [...new Set((ordersResult?.data || []).filter((o) => o?.user_id && !o?.guest_email).map((o) => o.user_id))];
      const userProfilesMap = {};
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase?.from('user_profiles')?.select('id, email')?.in('id', userIds);
        (profiles || [])?.forEach((p) => { userProfilesMap[p.id] = p; });
      }

      const registrationsMap = {};
      (registrationsResult?.data || [])?.forEach((r) => { registrationsMap[r.id] = r; });

      // Step 4: Manually merge and map
      const mapped = logsData?.map((log) => {
        const isEvent = !!log?.reservation_id;
        const type = isEvent ? 'EVENT' : 'STORE';

        const matchedOrder = log?.order_id ? ordersMap?.[log?.order_id] : null;
        const matchedRegistration = log?.reservation_id ? registrationsMap?.[log?.reservation_id] : null;

        const customerName = isEvent
          ? (matchedRegistration?.participant_name || log?.customer_name || '-')
          : (matchedOrder?.customer_name || matchedOrder?.full_name || log?.customer_name || '-');

        // Email resolution priority:
        // 1. invoice_logs.customer_email (most reliable, set at invoice creation time)
        // 2. orders.guest_email (for guest checkouts)
        // 3. user_profiles.email (for logged-in user orders)
        // 4. event_registrations.participant_email (for events)
        const resolvedGuestEmail = matchedOrder?.guest_email || '';
        const resolvedBillingEmail = matchedOrder?.billing_email || '';
        const resolvedLegacyEmail = matchedOrder?.email || '';
        const resolvedUserProfileEmail = (matchedOrder?.user_id ? userProfilesMap?.[matchedOrder?.user_id]?.email : '') || '';
        const customerEmail = isEvent
          ? (log?.customer_email || matchedRegistration?.participant_email || '')
          : (
              matchedOrder?.guest_email ||
              matchedOrder?.billing_email ||
              matchedOrder?.email ||
              resolvedUserProfileEmail ||
              log?.customer_email ||
              ''
            );

        return {
          id: `log_${log?.id}`,
          logId: log?.id,
          type,
          customerName,
          customerEmail,
          // Expose all raw email sources for EmailModal multi-tier resolver
          guest_email: resolvedGuestEmail,
          billing_email: resolvedBillingEmail,
          email: resolvedLegacyEmail,
          user_profiles: resolvedUserProfileEmail ? { email: resolvedUserProfileEmail } : null,
          full_name: matchedOrder?.full_name || log?.customer_name || '',
          docNo: log?.doc_no || '-',
          ettn: log?.ettn || '',
          amount: log?.total_amount,
          storedUrl: log?.pdf_url || null,
          createdAt: log?.created_at,
          status: log?.status,
          reservationId: log?.reservation_id || null,
          orderId: log?.order_id || null,
          invoiceEmailSentAt: log?.invoice_email_sent_at || null,
        };
      });

      setRows(mapped);

      const archived = mapped?.filter((r) => r?.storedUrl)?.length;
      const missing = mapped?.filter((r) => !r?.storedUrl)?.length;
      const store = mapped?.filter((r) => r?.type === 'STORE')?.length;
      const event = mapped?.filter((r) => r?.type === 'EVENT')?.length;
      setStats({ total: mapped?.length, archived, missing, store, event });
    } catch (err) {
      setError(err?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleReplacePdf = async (row, file) => {
    if (!file) return;
    if (file?.type !== 'application/pdf') {
      showToast('error', 'Sadece PDF dosyası yüklenebilir');
      return;
    }
    setReplacingPdfId(row?.id);
    try {
      const ettn = row?.ettn || row?.logId;
      const fileName = `unified_invoice_${ettn}.pdf`;

      const { error: uploadError } = await supabase?.storage?.from('invoices')?.upload(fileName, file, {
        upsert: true,
        contentType: 'application/pdf',
      });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase?.storage?.from('invoices')?.getPublicUrl(fileName);
      const publicUrl = urlData?.publicUrl;

      const { error: updateError } = await supabase
        ?.from('invoice_logs')
        ?.update({ pdf_url: publicUrl })
        ?.eq('id', row?.logId);
      if (updateError) throw updateError;

      setRows((prev) =>
        prev?.map((r) => (r?.id === row?.id ? { ...r, storedUrl: publicUrl } : r))
      );
      showToast('success', "Fatura PDF'i başarıyla güncellendi.");
    } catch (err) {
      showToast('error', 'PDF değiştirme hatası: ' + (err?.message || 'Bilinmeyen hata'));
    } finally {
      setReplacingPdfId(null);
      // Reset file input
      if (replaceFileRefs?.current?.[row?.id]) {
        replaceFileRefs.current[row.id].value = '';
      }
    }
  };

  const handleDeletePdf = async () => {
    if (!deletePdfConfirm) return;
    setDeletePdfLoading(true);
    try {
      const { error: updateError } = await supabase
        ?.from('invoice_logs')
        ?.update({ pdf_url: null })
        ?.eq('id', deletePdfConfirm?.logId);
      if (updateError) throw updateError;

      setRows((prev) =>
        prev?.map((r) => (r?.id === deletePdfConfirm?.id ? { ...r, storedUrl: null } : r))
      );
      setStats((prev) => ({
        ...prev,
        archived: Math.max(0, prev?.archived - 1),
        missing: prev?.missing + 1,
      }));
      setDeletePdfConfirm(null);
      showToast('success', 'Fatura PDF\'i başarıyla silindi.');
    } catch (err) {
      showToast('error', 'PDF silme hatası: ' + (err?.message || 'Bilinmeyen hata'));
    } finally {
      setDeletePdfLoading(false);
    }
  };

  const handlePdfUploadSuccess = (rowId, newUrl) => {
    setRows((prev) =>
      prev?.map((r) => (r?.id === rowId ? { ...r, storedUrl: newUrl } : r))
    );
    setStats((prev) => ({
      ...prev,
      archived: prev?.archived + 1,
      missing: Math.max(0, prev?.missing - 1),
    }));
  };

  const handleDeleteLog = async () => {
    if (!deleteConfirm) return;
    const logId = deleteConfirm?.logId;
    if (!logId) {
      showToast('error', 'Silinecek kayıt ID bulunamadı.');
      setTimeout(() => showToast(null), 3500);
      return;
    }
    setDeleteLoading(true);
    try {
      const { error, count } = await supabase
        ?.from('invoice_logs')
        ?.delete({ count: 'exact' })
        ?.eq('id', logId);
      if (error) throw error;
      if (count === 0) {
        throw new Error('Kayıt veritabanında bulunamadı veya silme izniniz yok.');
      }
      setRows((prev) => prev?.filter((r) => r?.id !== deleteConfirm?.id));
      setStats((prev) => ({
        ...prev,
        total: Math.max(0, prev?.total - 1),
        archived: deleteConfirm?.storedUrl ? Math.max(0, prev?.archived - 1) : prev?.archived,
        missing: !deleteConfirm?.storedUrl ? Math.max(0, prev?.missing - 1) : prev?.missing,
        store: deleteConfirm?.type === 'STORE' ? Math.max(0, prev?.store - 1) : prev?.store,
        event: deleteConfirm?.type === 'EVENT' ? Math.max(0, prev?.event - 1) : prev?.event,
      }));
      setDeleteConfirm(null);
      showToast('success', 'Kayıt başarıyla silindi');
      setTimeout(() => showToast(null), 3500);
    } catch (err) {
      console.error('Invoice log deletion failed:', err);
      showToast('error', err?.message || 'Silme işlemi başarısız');
      setTimeout(() => showToast(null), 3500);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEmailSentSuccess = (rowId, sentAt) => {
    const timestamp = sentAt || new Date()?.toISOString();
    setRows((prev) =>
      prev?.map((r) =>
        r?.id === rowId
          ? { ...r, invoiceEmailSentAt: timestamp, invoice_email_sent_at: timestamp, email_sent_at: timestamp, is_invoice_email_sent: true }
          : r
      )
    );
    showToast('success', 'E-posta başarıyla gönderildi!');
  };

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const filteredRows = rows?.filter((row) => {
    if (onlyMissingPdf && row?.storedUrl) return false;

    // Email filter
    if (emailFilter !== 'ALL') {
      const isSent = Boolean(row?.invoiceEmailSentAt || row?.invoice_email_sent_at || row?.email_sent_at || row?.is_invoice_email_sent);
      if (emailFilter === 'SENT' && !isSent) return false;
      if (emailFilter === 'UNSENT' && isSent) return false;
    }

    if (search?.trim()) {
      const q = search?.toLowerCase();
      const matches =
        row?.customerName?.toLowerCase()?.includes(q) ||
        row?.customerEmail?.toLowerCase()?.includes(q) ||
        row?.docNo?.toLowerCase()?.includes(q) ||
        row?.ettn?.toLowerCase()?.includes(q);
      if (!matches) return false;
    }

    if (dateFrom) {
      if (new Date(row.createdAt) < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      const end = new Date(dateTo);
      end?.setHours(23, 59, 59, 999);
      if (new Date(row.createdAt) > end) return false;
    }

    return true;
  });

  // ─── Checkbox Selection Handlers ───────────────────────────────────────────
  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedIds(filteredRows?.map((r) => r?.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ─── Bulk Email Send Handler ────────────────────────────────────────────────
  const handleBulkSendEmail = async () => {
    const validSelectedIds = selectedIds?.filter((id) => {
      const row = rows?.find((r) => r?.id === id);
      return row?.storedUrl;
    });
    const noPdfList = selectedIds?.filter((id) => {
      const row = rows?.find((r) => r?.id === id);
      return !row?.storedUrl;
    });

    if (noPdfList?.length > 0) {
      showToast('warning', `${noPdfList?.length} kayıtta PDF bulunamadı, atlandı.`);
    }

    if (validSelectedIds?.length === 0) {
      showToast('error', 'Seçili kayıtların hiçbirinde PDF bulunamadı. Önce PDF yükleyin.');
      return;
    }

    setIsSendingBulk(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of validSelectedIds) {
      const row = rows?.find((item) => item?.id === id);
      if (!row) { failCount++; continue; }

      try {
        const customerEmail =
          row?.guest_email ||
          row?.billing_email ||
          row?.email ||
          row?.user_profiles?.email ||
          row?.customerEmail ||
          '';

        if (!customerEmail) {
          console.error(`[BulkEmail] No email found for row ${id}`);
          failCount++;
          continue;
        }

        let body;
        if (row?.type === 'STORE') {
          body = JSON.stringify({
            logId: row?.logId,
            ettn: row?.ettn || undefined,
            docNo: row?.docNo || undefined,
            customerEmail,
          });
        } else {
          body = JSON.stringify({
            logId: row?.logId,
            ettn: row?.ettn || undefined,
            docNo: row?.docNo || undefined,
            customerEmail,
            reservationId: row?.reservationId || undefined,
          });
        }

        const response = await fetch(`${SUPABASE_URL}/functions/v1/send-invoice-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body,
        });

        const data = await response?.json();
        if (!response?.ok) throw new Error(data?.error || `HTTP ${response.status}`);

        // Update DB timestamp
        const nowISO = new Date()?.toISOString();
        await supabase?.from('invoice_logs')?.update({ invoice_email_sent_at: nowISO })?.eq('id', row?.logId);

        // Update local state immediately
        setRows((prev) =>
          prev?.map((r) => (r?.id === id ? { ...r, invoiceEmailSentAt: nowISO } : r))
        );
        successCount++;
      } catch (err) {
        console.error(`[BulkEmail] Email send failed for ${id}:`, err?.message || err);
        failCount++;
      }
    }

    setIsSendingBulk(false);
    setSelectedIds([]);
    showToast('success', `Toplu gönderim tamamlandı! ${successCount} e-posta başarıyla iletildi.`);
    if (failCount > 0) {
      setTimeout(() => showToast('error', `${failCount} kişiye e-posta gönderilemedi.`), 4000);
    }
  };

  // ─── Bulk PDF Upload Handler ────────────────────────────────────────────────
  const handleBulkUpload = async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files?.length) return;

    setBulkUploading(true);
    let successCount = 0;
    const failedFiles = [];
    const updatedUrls = {};

    for (const file of files) {
      if (file?.type !== 'application/pdf') {
        failedFiles?.push(file?.name);
        continue;
      }

      // 1. Remove the .pdf extension
      const fileNameWithoutExt = file?.name?.replace(/\.[^/.]+$/, '');

      // 2. Split by underscore
      const nameParts = fileNameWithoutExt?.split('_');

      // 3. Extract the actual customer name (pattern: DATE_INVNO_NAME -> index 2+)
      const extractedName = nameParts?.length >= 3
        ? nameParts?.slice(2)?.join('_')
        : fileNameWithoutExt;

      // 4. Normalize for matching
      const normalizedFileName = normalizeText(extractedName);

      // 5. Find the match
      const matchedRow = rows?.find(
        (row) => normalizeText(row?.customerName || row?.participantName) === normalizedFileName
      );

      if (!matchedRow) {
        failedFiles?.push(file?.name);
        continue;
      }

      try {
        const ettn = matchedRow?.ettn || matchedRow?.logId;
        const filePath = `orders/${matchedRow?.logId || matchedRow?.id}/${Date.now()}_${file?.name}`;

        const { data: uploadData, error: uploadError } = await supabase?.storage?.from('invoices')?.upload(filePath, file, {
            upsert: true,
            cacheControl: '3600',
            contentType: 'application/pdf',
          });
        if (uploadError) {
          console.error(`[BulkUpload] Storage upload failed for "${file?.name}":`, uploadError?.message, uploadError);
          throw uploadError;
        }

        const { data: urlData } = supabase?.storage?.from('invoices')?.getPublicUrl(filePath);
        const publicUrl = urlData?.publicUrl;

        const { error: updateError } = await supabase?.from('invoice_logs')?.update({ pdf_url: publicUrl })?.eq('id', matchedRow?.logId);
        if (updateError) {
          console.error(`[BulkUpload] DB update failed for "${file?.name}" (id: ${matchedRow?.logId}):`, updateError?.message, updateError);
          throw updateError;
        }

        updatedUrls[matchedRow?.id] = publicUrl;
        successCount++;
      } catch (err) {
        console.error(`[BulkUpload] Failed for "${file?.name}":`, err?.message || err);
        failedFiles?.push(file?.name);
      }
    }

    // Update local state for all matched rows at once
    if (Object.keys(updatedUrls)?.length > 0) {
      setRows((prev) =>
        prev?.map((r) =>
          updatedUrls?.[r?.id] ? { ...r, storedUrl: updatedUrls?.[r?.id] } : r
        )
      );
      setStats((prev) => {
        const newlyArchived = Object.keys(updatedUrls)?.length;
        return {
          ...prev,
          archived: prev?.archived + newlyArchived,
          missing: Math.max(0, prev?.missing - newlyArchived),
        };
      });
    }

    // Reset file input
    if (bulkUploadRef?.current) {
      bulkUploadRef.current.value = '';
    }

    setBulkUploading(false);

    // Show summary toasts
    if (successCount > 0) {
      showToast('success', `Toplu yükleme tamamlandı! ${successCount} fatura eşleştirildi.`);
    }
    if (failedFiles?.length > 0) {
      setTimeout(() => {
        showToast('warning', `Eşleşmeyen dosyalar: ${failedFiles?.join(', ')}`);
      }, successCount > 0 ? 4000 : 0);
    }
    if (successCount === 0 && failedFiles?.length === 0) {
      showToast('warning', 'Yüklenecek PDF dosyası bulunamadı.');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts)?.toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    if (amount == null) return '-';
    return `₺${parseFloat(amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
  };

  const pageContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          {!embedded && (
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate('/admin-dashboard')}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="ArrowLeft" size={18} />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Fatura Merkezi</h1>
            </div>
          )}
          {embedded && (
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Fatura Merkezi</h1>
          )}
          <p className={`text-sm text-muted-foreground ${!embedded ? 'ml-9' : 'mt-1'}`}>
            Mağaza ve etkinlik faturalarının merkezi yönetimi
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Bulk PDF Upload */}
          <input
            ref={bulkUploadRef}
            type="file"
            multiple
            accept="application/pdf"
            id="bulk-pdf-upload"
            className="hidden"
            onChange={handleBulkUpload}
          />
          <label
            htmlFor="bulk-pdf-upload"
            className={`inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-indigo-700 cursor-pointer transition-colors ${bulkUploading ? 'opacity-60 pointer-events-none' : ''}`}
          >
            {bulkUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Yükleniyor...
              </>
            ) : (
              <>
                <Icon name="UploadCloud" size={18} />
                Toplu PDF Yükle
              </>
            )}
          </label>
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={fetchData}
            disabled={loading}
          >
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Toplam Fatura', value: stats?.total, icon: 'FileText', color: 'bg-primary/10 text-primary' },
          { label: 'PDF Arşivlendi', value: stats?.archived, icon: 'CheckCircle', color: 'bg-green-100 text-green-700' },
          { label: 'PDF Eksik', value: stats?.missing, icon: 'AlertCircle', color: 'bg-red-100 text-red-700' },
          { label: 'Mağaza', value: stats?.store, icon: 'ShoppingBag', color: 'bg-blue-100 text-blue-700' },
          { label: 'Etkinlik', value: stats?.event, icon: 'Calendar', color: 'bg-purple-100 text-purple-700' },
        ]?.map((s) => (
          <div key={s?.label} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s?.color}`}>
              <Icon name={s?.icon} size={16} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground leading-tight">{s?.label}</p>
              <p className="text-xl font-bold text-foreground">{s?.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder="Müşteri, belge no, ETTN ara..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e?.target?.value)}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <span className="text-muted-foreground text-sm">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e?.target?.value)}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => setOnlyMissingPdf((v) => !v)}
              className={`w-10 h-5 rounded-full transition-colors relative ${onlyMissingPdf ? 'bg-red-500' : 'bg-muted'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${onlyMissingPdf ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-foreground font-medium">Sadece PDF Eksikleri</span>
          </label>
        </div>
      </div>

      {/* Email Status Filter */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-4 border border-gray-200">
        <button onClick={() => setEmailFilter('ALL')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${emailFilter === 'ALL' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Tümü
        </button>
        <button onClick={() => setEmailFilter('SENT')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${emailFilter === 'SENT' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Gönderilenler
        </button>
        <button onClick={() => setEmailFilter('UNSENT')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${emailFilter === 'UNSENT' ? 'bg-white text-rose-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Bekleyenler
        </button>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 px-4 py-2 rounded-lg mb-4 text-indigo-900 text-sm font-medium flex-wrap">
          <span>{selectedIds?.length} kayıt seçildi</span>
          <button
            onClick={handleBulkSendEmail}
            disabled={isSendingBulk}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-semibold transition-colors disabled:opacity-50"
          >
            <Icon name="Send" size={14} />
            {isSendingBulk ? 'Gönderiliyor...' : 'Seçilenlere E-Posta Gönder'}
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="text-xs text-indigo-600 hover:text-indigo-800 underline ml-auto"
          >
            Seçimi Temizle
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : filteredRows?.length === 0 ? (
          <div className="text-center py-16">
            <Icon name="FileSearch" size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground font-medium">Kayıt bulunamadı</p>
            <p className="text-xs text-muted-foreground mt-1">Filtrelerinizi değiştirmeyi deneyin</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-lg shadow ring-1 ring-black ring-opacity-5 pb-4">
            <table className="min-w-max w-full divide-y divide-gray-300 text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="sticky left-0 z-20 bg-white px-4 py-3 border-r border-gray-200 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds?.length === filteredRows?.length && filteredRows?.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">Tür</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">Müşteri</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">Belge No</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">ETTN</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">Tutar</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide z-10">Tarih</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">PDF Durumu</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">E-Posta Durumu</th>
                  <th className="sticky right-0 z-20 bg-white px-4 py-3 pl-6 border-l border-gray-200 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] text-right whitespace-nowrap min-w-[280px] w-72 text-xs font-semibold text-muted-foreground uppercase tracking-wide">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredRows?.map((row) => (
                  <tr key={row?.id} className={`hover:bg-muted/20 transition-colors ${selectedIds?.includes(row?.id) ? 'bg-indigo-50/50' : ''}`}>
                    <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds?.includes(row?.id)}
                        onChange={() => handleToggleSelect(row?.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TypeBadge type={row?.type} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground truncate max-w-[160px]">{row?.customerName}</p>
                      {row?.customerEmail && (
                        <p className="text-xs text-muted-foreground truncate max-w-[160px]">{row?.customerEmail}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground font-mono text-xs">{row?.docNo}</td>
                    <td className="px-4 py-3">
                      {row?.ettn ? (
                        <span className="font-mono text-xs text-muted-foreground truncate block max-w-[120px]" title={row?.ettn}>
                          {row?.ettn?.length > 16 ? row?.ettn?.substring(0, 16) + '…' : row?.ettn}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground">{formatAmount(row?.amount)}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatDate(row?.createdAt)}</td>
                    {/* 1. PDF STATUS */}
                    <td className="px-4 py-3 align-middle">
                      {row?.storedUrl ? (
                        <div className="flex justify-center" title="PDF Yüklendi">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 shadow-sm transition-transform hover:scale-105">
                            <FileText size={16} className="text-emerald-600" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-center" title="PDF Yüklenmedi">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-50 border border-rose-200 shadow-sm transition-transform hover:scale-105">
                            <FileX size={16} className="text-rose-500" />
                          </div>
                        </div>
                      )}
                    </td>

                    {/* 2. EMAIL STATUS */}
                    <td className="px-4 py-3 text-center align-middle whitespace-nowrap">
                      {(row?.invoiceEmailSentAt || row?.invoice_email_sent_at || row?.email_sent_at || row?.is_invoice_email_sent) ? (
                        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 border border-green-200 px-2.5 py-1 rounded-full text-xs font-semibold">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          E-Posta İletildi
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full text-xs font-medium">
                          <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                          Bekliyor
                        </span>
                      )}
                    </td>

                    {/* 3. ACTIONS */}
                    <td className="sticky right-0 z-10 bg-white px-4 py-3 pl-6 border-l border-gray-200 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] align-middle text-right whitespace-nowrap overflow-visible">
                      <div className="flex items-center justify-end gap-4 min-w-max whitespace-nowrap">
                        {/* Action Button Group */}
                        <div className="inline-flex rounded-md shadow-sm flex-shrink-0" role="group">
                          {/* Download button — only shown when PDF exists */}
                          {row?.storedUrl ? (
                            <a
                              href={row?.storedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="PDF'i İndir"
                              className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-50 hover:text-blue-600 focus:z-10 focus:ring-2 focus:ring-blue-500 transition-all"
                            >
                              <Icon name="Download" size={16} />
                            </a>
                          ) : (
                            <button
                              onClick={() => setUploadModal(row)}
                              title="PDF Yükle"
                              className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-50 hover:text-orange-600 focus:z-10 focus:ring-2 focus:ring-orange-500 transition-all"
                            >
                              <Icon name="Upload" size={16} />
                            </button>
                          )}

                          {/* Replace PDF button */}
                          <button
                            title="Faturayı Değiştir"
                            onClick={() => replaceFileRefs?.current?.[row?.id]?.click()}
                            disabled={replacingPdfId === row?.id}
                            className={`inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 bg-white border-t border-b border-gray-300 hover:bg-gray-50 hover:text-orange-600 focus:z-10 focus:ring-2 focus:ring-orange-500 transition-all ${replacingPdfId === row?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {replacingPdfId === row?.id ? (
                              <div className="w-4 h-4 border-2 border-orange-400/40 border-t-orange-600 rounded-full animate-spin" />
                            ) : (
                              <Icon name="RefreshCw" size={16} />
                            )}
                          </button>
                          <input
                            ref={(el) => { if (el) replaceFileRefs.current[row?.id] = el; }}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const f = e?.target?.files?.[0];
                              if (f) handleReplacePdf(row, f);
                            }}
                          />

                          {/* Send Email button */}
                          <button
                            title={row?.invoiceEmailSentAt ? 'Yeniden Gönder' : 'E-Posta Gönder'}
                            onClick={() => setEmailModal(row)}
                            className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-purple-600 focus:z-10 focus:ring-2 focus:ring-purple-500 transition-all"
                          >
                            <Icon name="Send" size={16} />
                          </button>

                          {/* Delete record button */}
                          <button
                            onClick={() => setDeleteConfirm(row)}
                            title="Kaydı Sil"
                            className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-50 hover:text-red-600 focus:z-10 focus:ring-2 focus:ring-red-500 transition-all"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredRows?.length > 0 && (
          <div className="px-4 py-3 border-t border-border bg-muted/20 text-xs text-muted-foreground">
            {filteredRows?.length} kayıt gösteriliyor
            {onlyMissingPdf && <span className="ml-2 text-red-600 font-medium">· Sadece PDF eksikler</span>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {!embedded && <Header />}
      {embedded ? (
        pageContent
      ) : (
        <main className="main-content">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            {pageContent}
          </div>
        </main>
      )}
      {uploadModal && (
        <PdfUploadModal
          row={uploadModal}
          onClose={() => setUploadModal(null)}
          onSuccess={handlePdfUploadSuccess}
        />
      )}
      {emailModal && (
        <EmailModal
          row={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={(rowId, sentAt) => {
            setEmailModal(null);
            setTimeout(() => {
              handleEmailSentSuccess(rowId, sentAt);
            }, 50);
          }}
        />
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
            <div className="flex items-center gap-3 p-5 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Kaydı Sil</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{deleteConfirm?.customerName} · {deleteConfirm?.docNo}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-foreground mb-6">
                Bu kaydı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteLog}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Icon name="Trash2" size={14} />
                  )}
                  Evet, Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete PDF Confirmation Modal */}
      {deletePdfConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
            <div className="flex items-center gap-3 p-5 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon name="FileX" size={20} className="text-orange-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Fatura PDF'ini Sil</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{deletePdfConfirm?.customerName} · {deletePdfConfirm?.docNo}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-foreground mb-6">
                Bu faturanın PDF dosyasını silmek istediğinizden emin misiniz? Fatura kaydı korunacak, yalnızca PDF bağlantısı kaldırılacaktır.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletePdfConfirm(null)}
                  disabled={deletePdfLoading}
                  className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleDeletePdf}
                  disabled={deletePdfLoading}
                  className="flex-1 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                >
                  {deletePdfLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Icon name="FileX" size={14} />
                  )}
                  Evet, PDF'i Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium max-w-sm ${
          toast?.type === 'error' ? 'bg-red-600 text-white' :
          toast?.type === 'warning'? 'bg-amber-500 text-white' : 'bg-green-600 text-white'
        }`}>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : toast?.type === 'warning' ? 'AlertTriangle' : 'CheckCircle'} size={16} className="flex-shrink-0" />
          <span className="break-words">{toast?.message}</span>
        </div>
      )}
    </div>
  );
};

export default MasterInvoiceCenter;
